import React, { useState } from 'react';
import {
  Footprints,
  ChevronDown,
  Target,
  Building2,
  Users,
  Image,
  Search,
  Globe,
  Newspaper,
  MapPin,
  FileCheck,
  Flag,
  AlertCircle,
  LucideIcon,
} from 'lucide-react';
import CompaniesHouseLookup from '../components/CompaniesHouseLookup';
import SocmintLookup from '../components/SocmintLookup';
import DorkBuilder from '../components/DorkBuilder';
import NewsMediaLookup from '../components/NewsMediaLookup';
import CybintLookup from '../components/CybintLookup';

interface SubSection {
  title: string;
  intro: string;
  points: string[];
}

interface Step {
  phase: string;
  title: string;
  icon: LucideIcon;
  description: string;
  actions?: string[];
  subsections?: SubSection[];
}

const steps: Step[] = [
  {
    phase: 'Scoping',
    title: 'Define the objective',
    icon: Target,
    description:
      'Before touching a single tool, pin down exactly what you are trying to learn and where the boundaries are.',
    actions: [
      'Write a one-sentence intelligence requirement.',
      'List what you already know — the "seed" data (a name, email, username, or company).',
      'Set legal and ethical limits for the engagement.',
    ],
  },
  {
    phase: 'Corporate',
    title: 'Companies House & corporate records',
    icon: Building2,
    description:
      'If a company or business is involved, establish the legal entity, its officers, and its paper trail first.',
    subsections: [
      {
        title: 'Companies House (UK)',
        intro: 'The primary UK registry for company data — free and authoritative.',
        points: [
          'Search the company by name or number at find-and-update.company-information.service.gov.uk.',
          'Record the registered address, incorporation date, and SIC (industry) codes.',
          'Download filing history — accounts, confirmation statements, and charges.',
          'Note the officers: directors, secretaries, and their appointment dates.',
        ],
      },
      {
        title: 'People & Directors',
        intro: 'Pivot from the company to the individuals behind it.',
        points: [
          'Open each officer profile to list their other current and past appointments.',
          'Check Persons with Significant Control (PSC) for real ownership.',
          'Cross-reference director names, DOB (month/year), and correspondence addresses.',
        ],
      },
      {
        title: 'International Registries',
        intro: 'For non-UK entities, use equivalent registries and aggregators.',
        points: [
          'OpenCorporates for cross-jurisdiction company data.',
          'SEC EDGAR for US public-company filings.',
          'Local registries (e.g. Handelsregister, INPI) for regional entities.',
        ],
      },
    ],
  },
  {
    phase: 'SOCMINT',
    title: 'Map the social footprint',
    icon: Users,
    description:
      'Start from the seed identifier — a username, email, or name — and expand across social platforms.',
    subsections: [
      {
        title: 'LinkedIn',
        intro: 'Best source for employees, roles, and organisational structure.',
        points: [
          'List current employees and their job titles to map the org chart.',
          'Track role changes and tenure from work history.',
          'Note skills, endorsements, and mutual connections.',
        ],
      },
      {
        title: 'X / Instagram / Facebook / TikTok',
        intro: 'Personal accounts reveal activity, location, and network.',
        points: [
          'Pivot a username across platforms (Sherlock, WhatsMyName).',
          'Review post history, tagged locations, and timestamps.',
          'Map followers/following for the wider network.',
        ],
      },
      {
        title: 'Handle & Username Pivots',
        intro: 'A single reused handle can unlock many accounts.',
        points: [
          'Feed the seed username into username-enumeration tools.',
          'Search the handle in quotes across search engines.',
          'Check for the same avatar via reverse image search.',
        ],
      },
    ],
  },
  {
    phase: 'IMINT',
    title: 'Verify with images',
    icon: Image,
    description:
      'Use profile pictures and posted media to confirm identity and surface new leads.',
    actions: [
      'Run reverse image searches (Google Lens, Yandex, TinEye).',
      'Inspect EXIF metadata where available.',
      'Cross-reference faces and backgrounds across accounts.',
    ],
  },
  {
    phase: 'Search',
    title: 'Google dorking',
    icon: Search,
    description:
      'Use advanced search operators to find what normal searches miss — documents, exposed files, and stray mentions.',
    subsections: [
      {
        title: 'Core Operators',
        intro: 'Combine operators to zero in on precise results.',
        points: [
          'site: — restrict results to one domain (site:example.com).',
          'filetype: — find documents (filetype:pdf, filetype:xlsx).',
          'intitle: / inurl: — match words in the page title or URL.',
          '"exact phrase" and - to exclude terms.',
        ],
      },
      {
        title: 'Exposed Files & Directories',
        intro: 'Surface documents that were never meant to be indexed.',
        points: [
          'intitle:"index of" to find open directory listings.',
          'site:example.com filetype:pdf confidential for stray documents.',
          'Search for spreadsheets and config files by extension.',
        ],
      },
      {
        title: 'Cross-Engine Dorking',
        intro: 'Different engines index different content.',
        points: [
          'Repeat key dorks on Bing, DuckDuckGo, and Yandex.',
          'Yandex often surfaces content and images others miss.',
          'Keep a saved list of dorks per investigation.',
        ],
      },
    ],
  },
  {
    phase: 'CYBINT',
    title: 'Trace the digital infrastructure',
    icon: Globe,
    description:
      'Move from people to the systems they use — domains, emails, and breach exposure.',
    subsections: [
      {
        title: 'WHOIS & DNS',
        intro: 'Identify who registered a domain and where it points.',
        points: [
          'Run WHOIS for registrant, dates, and registrar.',
          'Enumerate DNS records (A, MX, TXT) and subdomains.',
          'Pivot on shared registrant emails or nameservers.',
        ],
      },
      {
        title: 'Breach & Email Exposure',
        intro: 'Check whether identifiers appear in known breaches.',
        points: [
          'Query emails in Have I Been Pwned.',
          'Correlate breached data with other identifiers.',
        ],
      },
    ],
  },
  {
    phase: 'Media',
    title: 'News & media',
    icon: Newspaper,
    description:
      'Corroborate findings and surface context through published reporting.',
    subsections: [
      {
        title: 'News Aggregators',
        intro: 'Search broadly, then narrow by date and outlet.',
        points: [
          'Use Google News and site-specific searches for the entity/person.',
          'Filter by date range to build a timeline of events.',
          'Prefer primary reporting over aggregated re-writes.',
        ],
      },
      {
        title: 'Archives & Wayback',
        intro: 'Recover deleted or changed pages.',
        points: [
          'Check the Wayback Machine for prior versions of a site or profile.',
          'Use archive.today for point-in-time snapshots.',
          'Compare versions to spot edits or removed claims.',
        ],
      },
      {
        title: 'Press Releases & PR',
        intro: 'Company-issued material reveals partnerships and personnel.',
        points: [
          'Read the company newsroom and PR wire distributions.',
          'Note named executives, partners, and quoted spokespeople.',
        ],
      },
    ],
  },
  {
    phase: 'GEOINT',
    title: 'Establish location',
    icon: MapPin,
    description:
      'Combine visual and metadata clues to place the subject in the physical world.',
    actions: [
      'Chronolocate using shadows, signage, and landmarks.',
      'Corroborate with satellite and street-view imagery.',
      'Build a timeline of movements from timestamps.',
    ],
  },
  {
    phase: 'Analysis',
    title: 'Corroborate & document',
    icon: FileCheck,
    description:
      'Intelligence is only useful if it holds up. Confirm every finding against a second source.',
    subsections: [
      {
        title: 'Source Corroboration',
        intro: 'No single source is enough on its own.',
        points: [
          'Require at least two independent sources per claim.',
          'Record confidence level and any assumptions made.',
        ],
      },
      {
        title: 'Evidence Handling',
        intro: 'Preserve what you find so it holds up later.',
        points: [
          'Screenshot with visible URL and timestamp.',
          'Save archived copies and note the collection date.',
          'Keep a clear, reproducible method trail.',
        ],
      },
    ],
  },
  {
    phase: 'Reporting',
    title: 'Deliver the findings',
    icon: Flag,
    description:
      'Package the investigation into a clear, reproducible report an analyst can act on.',
    actions: [
      'Summarize conclusions and supporting evidence.',
      'Include a reproducible method trail.',
      'State remaining gaps and next steps.',
    ],
  },
];

interface AccordionRowProps {
  sub: SubSection;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionRow: React.FC<AccordionRowProps> = ({ sub, isOpen, onToggle }) => (
  <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-slate-800">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors"
    >
      <span className="font-semibold text-slate-900 dark:text-slate-100">{sub.title}</span>
      <ChevronDown
        className={`w-5 h-5 text-orange-500 flex-shrink-0 transition-transform duration-300 ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
    </button>
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="overflow-hidden">
        <div className="px-4 pb-3 pt-0">
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">{sub.intro}</p>
          <ul className="space-y-2">
            {sub.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-gray-700 dark:text-slate-300">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const Walkthrough: React.FC = () => {
  // key = `${stepIndex}-${subIndex}`; first dropdown of the first step-with-dropdowns open by default
  const [open, setOpen] = useState<Record<string, boolean>>({ '1-0': true });

  const toggle = (key: string) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 py-8 px-6">
      <div className="max-w-none">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white">
            <Footprints className="w-7 h-7" />
          </div>
          <div>
            <p className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-1">
              Start to Finish
            </p>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">OSINT Walkthrough</h1>
          </div>
        </div>
        <p className="text-gray-500 dark:text-slate-400 max-w-2xl mb-8">
          A complete investigation, phase by phase — from a single seed identifier to a
          documented report. Work top to bottom and expand each source for the exact methods.
        </p>

        <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-10" />

        {/* Ethics note */}
        <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-500/10 p-6 mb-12">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-2">Before you begin</h3>
              <p className="text-gray-700 dark:text-slate-300">
                Only investigate targets you are authorized to research. Stay within legal
                boundaries, protect your own operational security, and document your sources
                as you go.
              </p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* vertical spine */}
          <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-gradient-to-b from-orange-500 to-amber-600" />

          <div className="space-y-6">
            {steps.map((step, si) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="relative flex items-start gap-6 group">
                  {/* node */}
                  <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* card */}
                  <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 transition-all duration-300 group-hover:shadow-lg group-hover:border-orange-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold tracking-widest text-orange-500 uppercase">
                        Step {si + 1} · {step.phase}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1">{step.title}</h3>
                    <p className="text-gray-600 dark:text-slate-400 mb-4 leading-relaxed">{step.description}</p>

                    {/* quick action bullets */}
                    {step.actions && (
                      <ul className="space-y-2">
                        {step.actions.map((action) => (
                          <li key={action} className="flex items-start gap-3 text-sm text-gray-700 dark:text-slate-300">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* expandable method dropdowns */}
                    {step.subsections && (
                      <div className="space-y-2">
                        {step.subsections.map((sub, ci) => {
                          const key = `${si}-${ci}`;
                          return (
                            <AccordionRow
                              key={key}
                              sub={sub}
                              isOpen={!!open[key]}
                              onToggle={() => toggle(key)}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* live, auto-run check for the Companies House step */}
                    {step.phase === 'Corporate' && <CompaniesHouseLookup />}

                    {/* live username enumeration for the SOCMINT step */}
                    {step.phase === 'SOCMINT' && <SocmintLookup />}

                    {/* parameter-driven dork builder for the Google dorking step */}
                    {step.phase === 'Search' && <DorkBuilder />}

                    {/* AI web-search briefing for the News & media step */}
                    {step.phase === 'Media' && <NewsMediaLookup />}

                    {/* AI WHOIS/DNS + breach-exposure briefing for the CYBINT step */}
                    {step.phase === 'CYBINT' && <CybintLookup />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Walkthrough;
